import React, { useState } from 'react';
import { BookOpen, CheckCircle, Lock, Star, Trees, Mountain, Castle, Flag, Heart, Brain, Lightbulb, Users, Compass, Rocket } from 'lucide-react';
import { Lesson } from '../types';
import LessonModal from './LessonModal';

interface LessonsProps {
  lessons: Lesson[];
  onSelectLesson: (lesson: Lesson, earnedXp: number) => void;
}

const Lessons: React.FC<LessonsProps> = ({ lessons, onSelectLesson }) => {
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);

  const handleLessonComplete = (lesson: Lesson, earnedXp: number) => {
    onSelectLesson(lesson, earnedXp);
    setSelectedLesson(null);
  };

  const categoryColors = {
    mental_resilience: {
      primary: 'bg-purple-500',
      secondary: 'bg-purple-100',
      text: 'text-purple-600',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-50',
      icon: <Brain className="w-6 h-6" />,
    },
    emotional_intelligence: {
      primary: 'bg-rose-500',
      secondary: 'bg-rose-100',
      text: 'text-rose-600',
      border: 'border-rose-200',
      hover: 'hover:bg-rose-50',
      icon: <Heart className="w-6 h-6" />,
    },
    productivity: {
      primary: 'bg-amber-500',
      secondary: 'bg-amber-100',
      text: 'text-amber-600',
      border: 'border-amber-200',
      hover: 'hover:bg-amber-50',
      icon: <Lightbulb className="w-6 h-6" />,
    },
    relationship_social: {
      primary: 'bg-blue-500',
      secondary: 'bg-blue-100',
      text: 'text-blue-600',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-50',
      icon: <Users className="w-6 h-6" />,
    },
    purpose_meaning: {
      primary: 'bg-emerald-500',
      secondary: 'bg-emerald-100',
      text: 'text-emerald-600',
      border: 'border-emerald-200',
      hover: 'hover:bg-emerald-50',
      icon: <Compass className="w-6 h-6" />,
    },
    personal_growth: {
      primary: 'bg-indigo-500',
      secondary: 'bg-indigo-100',
      text: 'text-indigo-600',
      border: 'border-indigo-200',
      hover: 'hover:bg-indigo-50',
      icon: <Rocket className="w-6 h-6" />,
    },
  };

  // Sort lessons by order
  const sortedLessons = [...lessons].sort((a, b) => a.order - b.order);

  // Check if a lesson is locked (can't be accessed yet)
  const isLessonLocked = (lesson: Lesson) => {
    if (lesson.order === 1) return false;
    const previousLesson = sortedLessons.find(l => l.order === lesson.order - 1);
    return !previousLesson?.completed;
  };

  return (
    <div className="bg-gradient-to-br from-sky-100 to-indigo-100 rounded-xl shadow-lg p-6 w-full max-h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-indigo-900">Growth Journey</h2>
        <div className="px-4 py-2 bg-white rounded-full shadow-md text-sm font-medium text-indigo-600">
          Level 1: Personal Development
        </div>
      </div>
      
      <div className="relative max-w-4xl mx-auto pb-8">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <Trees className="absolute top-10 left-10 w-8 h-8 text-emerald-500 opacity-30" />
          <Mountain className="absolute bottom-20 left-30 w-10 h-10 text-slate-400 opacity-20" />
          <Castle className="absolute top-40 right-40 w-8 h-8 text-amber-500 opacity-30" />
        </div>
        
        {/* Lesson nodes */}
        <div className="relative z-10 space-y-12">
          {sortedLessons.map((lesson, index) => {
            const locked = isLessonLocked(lesson);
            const colors = categoryColors[lesson.category];
            const isEvenIndex = index % 2 === 0;
            
            return (
              <div 
                key={lesson.id} 
                className={`
                  flex items-center gap-6
                  ${isEvenIndex ? 'justify-start' : 'justify-end'}
                `}
                style={{ 
                  marginLeft: `${isEvenIndex ? index * 5 : 0}%`, 
                  marginRight: `${!isEvenIndex ? index * 5 : 0}%` 
                }}
              >
                {/* Node */}
                <div 
                  className={`
                    relative group flex-shrink-0
                    ${isEvenIndex ? 'order-1' : 'order-2'}
                  `}
                >
                  <button 
                    className={`
                      w-16 h-16 rounded-2xl rotate-45 transform transition-all duration-300
                      ${locked ? 'bg-gray-200' : lesson.completed ? colors.primary : 'bg-white border-2 ' + colors.border}
                      ${!locked && !lesson.completed ? 'cursor-pointer group-hover:scale-110 group-hover:rotate-[135deg]' : ''}
                      shadow-lg
                    `}
                    onClick={() => !locked && !lesson.completed && setSelectedLesson(lesson)}
                    disabled={locked || lesson.completed}
                  >
                    <div className="absolute inset-0 flex items-center justify-center -rotate-45">
                      {locked ? (
                        <Lock className="w-6 h-6 text-gray-400" />
                      ) : lesson.completed ? (
                        <CheckCircle className="w-6 h-6 text-white" />
                      ) : (
                        <div className={colors.text}>{colors.icon}</div>
                      )}
                    </div>
                  </button>

                  {/* Stars for completed lessons */}
                  {lesson.completed && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex space-x-1">
                      {[1, 2, 3].map((star) => (
                        <Star 
                          key={star}
                          className={`w-4 h-4 ${
                            star <= lesson.stars
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Info card */}
                <div 
                  className={`
                    w-72 p-5 rounded-xl
                    ${locked ? 'bg-gray-50' : 'bg-white'}
                    shadow-lg transform transition-transform duration-300
                    ${!locked && !lesson.completed ? 'group-hover:scale-105' : ''}
                    ${isEvenIndex ? 'order-2' : 'order-1'}
                  `}
                >
                  <h3 className={`font-bold text-lg mb-2 ${locked ? 'text-gray-400' : 'text-gray-800'}`}>
                    {lesson.title}
                  </h3>
                  <p className={`text-sm mb-4 ${locked ? 'text-gray-400' : 'text-gray-600'}`}>
                    {lesson.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className={`
                      text-sm px-3 py-1.5 rounded-full font-medium
                      ${locked ? 'bg-gray-100 text-gray-400' : colors.secondary + ' ' + colors.text}
                    `}>
                      +{lesson.xpReward} XP
                    </div>
                    <span className={`text-sm ${locked ? 'text-gray-400' : 'text-gray-500'}`}>
                      {lesson.duration}
                    </span>
                  </div>
                  {lesson.progress > 0 && !lesson.completed && (
                    <div className="mt-4">
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${colors.primary}`}
                          style={{ width: `${lesson.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedLesson && (
        <LessonModal
          lesson={selectedLesson}
          onClose={() => setSelectedLesson(null)}
          onComplete={handleLessonComplete}
        />
      )}
    </div>
  );
};

export default Lessons;